import AutoCompleteChip from '@components/standardAutoCompleteSearch/AutoCompleteChip'
import StandardAutoCompleteSearch, {
  AutoCompleteOption,
  AutoCompleteResultsZIndexPlacement,
} from '@components/standardAutoCompleteSearch/StandardAutoCompleteSearch'
import Stack from '@mui/material/Stack'
import UserAvatar from '@shared/avatar/UserAvatar'
import { fullNameFromParts, fullNameWithEmail } from '@shared/utils/nameFormatting'
import { t } from '@transifex/native'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldName,
  FieldPathValue,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import ErrorText from '../ErrorText'
import FieldLabel from '../FieldLabel'
import HelpText from '../HelpText'
import useSearchTenantMembers, { TenantMemberSearchFn } from './useSearchTenantMembers'

type TenantMemberSearchFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  searchFn: TenantMemberSearchFn
  label?: ReactNode
  help?: ReactNode
  includeEmail?: boolean
  multiple?: boolean
  showAvatar?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
}

const TenantMemberSearchField = <T extends FieldValues>({
  name,
  control,
  searchFn,
  label,
  help,
  includeEmail = false,
  multiple = true,
  showAvatar = false,
  disabled = false,
  zIndexPlacement,
}: TenantMemberSearchFieldProps<T>) => {
  const [searchText, setSearchText] = useState<string>('')
  const { setValue } = useFormContext()
  const [options, setOptions] = useState<AutoCompleteOption[]>([])

  const selectedMembers = useWatch({ control, name })
  const selectedMembersIds = useMemo(
    () => selectedMembers?.map(({ id }: { id: string }) => id),
    [selectedMembers],
  )

  const { searchedTenantMembers } = useSearchTenantMembers(searchText, selectedMembersIds, searchFn)

  const onSelectedOptionsChanged = (options: AutoCompleteOption[]) => {
    const ids = options.map(options => options.id)
    const newMembers: FieldPathValue<FieldValues, FieldName<FieldValues>> = [
      ...selectedMembers,
      ...(searchedTenantMembers ?? []),
    ].filter((value: { id: string }) => ids.includes(value.id))
    setValue(name, newMembers, { shouldValidate: true, shouldDirty: true })
  }

  useEffect(() => {
    setOptions(
      searchedTenantMembers?.map(member => ({
        value: includeEmail
          ? fullNameWithEmail(member.firstName, member.lastName, member.email)
          : fullNameFromParts(member.firstName, member.lastName),
        id: member.id,
      })) || [],
    )
  }, [searchedTenantMembers])

  const getValue = (): AutoCompleteOption[] | AutoCompleteOption => {
    if (multiple) {
      return selectedMembers?.map(
        (member: PathValue<T, Path<T>>) =>
          ({
            value: includeEmail
              ? fullNameWithEmail(member.firstName, member.lastName, member.email)
              : fullNameFromParts(member.firstName, member.lastName),
            id: member.id,
            avatar: showAvatar && (
              <UserAvatar
                size={25}
                firstName={member.firstName}
                lastName={member.lastName}
                media={member.avatarUrls}
                avatarBoxStyle={{ display: 'flex' }}
              />
            ),
          }) as AutoCompleteOption,
      )
    } else {
      const member = selectedMembers[0] ?? null
      return (
        member &&
        ({
          id: member.id,
          value: includeEmail
            ? fullNameWithEmail(member.firstName, member.lastName, member.email)
            : fullNameFromParts(member.firstName, member.lastName),
          avatar: (
            <UserAvatar
              size={25}
              firstName={member.firstName}
              lastName={member.lastName}
              media={member.avatarUrls}
              avatarBoxStyle={{ display: 'flex' }}
            />
          ),
        } as AutoCompleteOption)
      )
    }
  }
  return (
    <Stack>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            {!!label && (
              <FieldLabel htmlFor={name} labelStyle={{ mb: !!help ? 0 : 1 }}>
                {label}
              </FieldLabel>
            )}
            {!!help && <HelpText>{help}</HelpText>}
            <StandardAutoCompleteSearch
              searchText={searchText}
              value={getValue()}
              options={options}
              onSearchTextChanged={setSearchText}
              onSelectedOptionsChanged={onSelectedOptionsChanged}
              placeholder={multiple ? t('Search users...') : t('Search for a single user...')}
              zIndexPlacement={zIndexPlacement}
              renderChip={(option, props, index) => (
                <AutoCompleteChip
                  {...props}
                  key={option.value + index}
                  label={option.value}
                  avatar={option.avatar}
                />
              )}
              multiple={multiple}
              disabled={disabled}
            />
            {!!error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Stack>
  )
}

export default TenantMemberSearchField
